import { useState, useEffect, useRef } from 'react';
import type { VisitTemplate } from '../data/templates';
import { AVAILABLE_ASSESSMENTS } from '../data/templates';

interface Props {
  template: VisitTemplate;
  signed: boolean;
}

export function EncounterTab({ template, signed }: Props) {
  const assessmentDefault = template.assessmentLines.map((line, i) => `${i + 1}. ${line}`).join('\n');
  const planDefault = template.planItems.map((item, i) => `${i + 1}. ${item}`).join('\n');

  const [subjective, setSubjective] = useState(template.subjective);
  const [objective, setObjective] = useState(template.objective);
  const [assessment, setAssessment] = useState(assessmentDefault);
  const [plan, setPlan] = useState(planDefault);
  const [selectedAssessments, setSelectedAssessments] = useState<string[]>(template.orderedAssessments);
  const [popupOpen, setPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // ICD-10 state
  const [icdCodes, setIcdCodes] = useState(template.icdCodes);
  const [icdPopupOpen, setIcdPopupOpen] = useState(false);
  const [icdCodeInput, setIcdCodeInput] = useState('');
  const icdPopupRef = useRef<HTMLDivElement>(null);

  // Track which assessment chips have been "used" (addressed in assessment text)
  const [usedChips, setUsedChips] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSubjective(template.subjective);
    setObjective(template.objective);
    setAssessment(template.assessmentLines.map((line, i) => `${i + 1}. ${line}`).join('\n'));
    setPlan(template.planItems.map((item, i) => `${i + 1}. ${item}`).join('\n'));
    setSelectedAssessments(template.orderedAssessments);
    setIcdCodes(template.icdCodes);
    setPopupOpen(false);
    setSearchQuery('');
    setIcdPopupOpen(false);
    setIcdCodeInput('');
    setUsedChips(new Set());
  }, [template]);

  // Listen for claryn-action events from the extension to fill fields directly
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { type: 'insert-field'; field: 'subjective' | 'assessment' | 'plan'; text: string }
        | { type: 'add-diagnosis'; code: string; description: string };
      if (!detail) return;
      if (detail.type === 'insert-field') {
        if (detail.field === 'subjective') setSubjective(detail.text);
        else if (detail.field === 'assessment') setAssessment(detail.text);
        else if (detail.field === 'plan') setPlan(detail.text);
      } else if (detail.type === 'add-diagnosis') {
        setIcdCodes((prev) => {
          if (prev.some((c) => c.code === detail.code)) return prev;
          return [...prev, { code: detail.code, description: detail.description }];
        });
      }
    };
    document.addEventListener('claryn-action', handler);
    return () => document.removeEventListener('claryn-action', handler);
  }, []);

  // Detect which chips are addressed when assessment text changes
  useEffect(() => {
    if (!assessment.trim()) {
      setUsedChips(new Set());
      return;
    }
    const lower = assessment.toLowerCase();
    const used = new Set<string>();
    for (const name of selectedAssessments) {
      // Simple keyword match: check if the chip name (or key part) appears in assessment
      const keywords = name.toLowerCase().split(/[\s\-/()]+/).filter(w => w.length > 2);
      const matched = keywords.some(kw => lower.includes(kw));
      if (matched) used.add(name);
    }
    setUsedChips(used);
  }, [assessment, selectedAssessments]);

  useEffect(() => {
    if (!popupOpen) return;
    searchInputRef.current?.focus();

    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setPopupOpen(false);
        setSearchQuery('');
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setPopupOpen(false); setSearchQuery(''); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [popupOpen]);

  useEffect(() => {
    if (!icdPopupOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (icdPopupRef.current && !icdPopupRef.current.contains(e.target as Node)) {
        setIcdPopupOpen(false);
        setIcdCodeInput('');
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setIcdPopupOpen(false); setIcdCodeInput(''); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [icdPopupOpen]);

  const removeAssessment = (name: string) => {
    setSelectedAssessments((prev) => prev.filter((a) => a !== name));
  };

  const addAssessment = (name: string) => {
    setSelectedAssessments((prev) => [...prev, name]);
    setPopupOpen(false);
    setSearchQuery('');
  };

  const filteredAssessments = AVAILABLE_ASSESSMENTS.filter(
    (a) =>
      !selectedAssessments.includes(a) &&
      a.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const removeIcd = (code: string) => {
    setIcdCodes((prev) => prev.filter((c) => c.code !== code));
  };

  const addIcd = () => {
    const code = icdCodeInput.trim();
    if (!code) return;
    if (icdCodes.some((c) => c.code === code)) return;
    setIcdCodes((prev) => [...prev, { code, description: '' }]);
    setIcdPopupOpen(false);
    setIcdCodeInput('');
  };

  return (
    <div className="tab-panel" data-claryn-signed={signed ? 'true' : undefined}>
      {/* Encounter details header */}
      <section className="encounter-details">
        <div className="encounter-details-item">
          <span className="encounter-details-label">CPT</span>
          <span className="encounter-details-value">{template.cptCode}</span>
        </div>
        <div className="encounter-details-item">
          <span className="encounter-details-label">Note type</span>
          <span className="encounter-details-value">SOAP Note</span>
        </div>
        <div className="encounter-details-item">
          <span className="encounter-details-label">Date</span>
          <span className="encounter-details-value">{template.visitDate}</span>
        </div>
      </section>

      {/* Chief complaint */}
      <section className="card">
        <h3 className="card-title">Chief Complaint</h3>
        <div className="card-body-text">{template.chiefComplaint}</div>
      </section>

      {/* Medications (duplicated under Chief Complaint for Visit tab) */}
      {template.medications.length > 0 && (
        <section className="card">
          <h3 className="card-title">Medications</h3>
          <table className="med-table">
            <thead>
              <tr>
                <th>Medication</th>
                <th>Sig</th>
                <th>Start</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {template.medications.map((med, i) => (
                <tr key={i}>
                  <td>{med.name}</td>
                  <td>{med.sig}</td>
                  <td>{med.startDate ?? '—'}</td>
                  <td>
                    <span className={`status-badge status-${med.status}`}>
                      {med.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Patient-level diagnoses (duplicated into Visit tab) */}
      {template.diagnoses && template.diagnoses.length > 0 && (
        <section className="card">
          <h3 className="card-title">Patient Diagnoses</h3>
          <table className="med-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {template.diagnoses.map((dx) => (
                <tr key={dx.code}>
                  <td className="icd-code">{dx.code}</td>
                  <td>{dx.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="card" data-claryn-section="subjective">
        <h3 className="card-title">Subjective</h3>
        <textarea
          className="soap-textarea"
          data-claryn-field="subjective"
          value={subjective}
          onChange={(e) => setSubjective(e.target.value)}
          disabled={signed}
        />
      </section>

      <section className="card">
        <h3 className="card-title">Objective</h3>
        <textarea
          className="soap-textarea"
          data-claryn-field="objective"
          value={objective}
          onChange={(e) => setObjective(e.target.value)}
          disabled={signed}
        />
      </section>

      {/* Diagnoses attached to encounter */}
      <section className="card" data-claryn-section="diagnoses">
        <h3 className="card-title">Diagnoses</h3>
        {icdCodes.length > 0 && (
          <table className="med-table">
            <thead>
              <tr>
                <th>Code</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {icdCodes.map((icd) => (
                <tr key={icd.code}>
                  <td className="icd-code">{icd.code}</td>
                  <td>
                    {!signed && (
                      <button
                        className="icd-remove-btn"
                        onClick={() => removeIcd(icd.code)}
                        aria-label={`Remove ${icd.code}`}
                      >
                        &times;
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!signed && (
          <div className="icd-add-area">
            <div className="assessment-popup-wrapper" ref={icdPopupRef}>
              <button
                className="add-assessment-btn"
                onClick={() => setIcdPopupOpen(true)}
              >
                + Add ICD-10
              </button>
              {icdPopupOpen && (
                <div className="assessment-popup icd-add-popup">
                  <input
                    className="assessment-popup-input"
                    type="text"
                    placeholder="Code (e.g. E55.9)"
                    value={icdCodeInput}
                    onChange={(e) => setIcdCodeInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addIcd(); }}
                    autoFocus
                  />
                  <div style={{ padding: '8px 12px' }}>
                    <button
                      className="icd-add-confirm-btn"
                      onClick={addIcd}
                      disabled={!icdCodeInput.trim()}
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Assessment note */}
      <section className="card" data-claryn-section="assessment">
        <h3 className="card-title">Assessment</h3>
        <textarea
          className="soap-textarea"
          data-claryn-field="assessment"
          value={assessment}
          onChange={(e) => setAssessment(e.target.value)}
          disabled={signed}
        />
        <div className="assessment-chips">
          {selectedAssessments.map((name) => (
            <span key={name} className={`assessment-chip${usedChips.has(name) ? ' used' : ''}`}>
              {name}
              {!signed && (
                <button
                  className="assessment-chip-remove"
                  onClick={() => removeAssessment(name)}
                  aria-label={`Remove ${name}`}
                >
                  &times;
                </button>
              )}
            </span>
          ))}
          {!signed && (
            <div className="assessment-popup-wrapper" ref={popupRef}>
              <button
                className="add-assessment-btn"
                onClick={() => setPopupOpen(true)}
              >
                + Add
              </button>
              {popupOpen && (
                <div className="assessment-popup">
                  <input
                    ref={searchInputRef}
                    className="assessment-popup-input"
                    type="text"
                    placeholder="Search assessments..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="assessment-popup-list">
                    {filteredAssessments.map((name) => (
                      <div
                        key={name}
                        className="assessment-popup-item"
                        onClick={() => addAssessment(name)}
                      >
                        {name}
                      </div>
                    ))}
                    {filteredAssessments.length === 0 && (
                      <div className="assessment-popup-empty">No results</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Plan note */}
      <section className="card" data-claryn-section="plan">
        <h3 className="card-title">Plan</h3>
        <textarea
          className="soap-textarea"
          data-claryn-field="plan"
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          disabled={signed}
        />
      </section>

      {/* Quality of care */}
      <section className="card">
        <h3 className="card-title">Quality of Care</h3>
        <ul className="quality-list">
          {template.qualityFlags.map((flag, i) => (
            <li key={i}>
              <span className="quality-check">{'\u2713'}</span>
              {flag}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
